// OriginForm.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Modal,
  Alert,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps, type FieldProps } from 'formik';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import JsonForm, { JsonFormTemplate } from '../../common/JsonForm';
import { useAuthStore } from '../../../store/useAuthStore3';
import { Origin } from '../../../types/origin';
import { OriginType } from '../../../types/originType';
import { Entity } from '../../../types/entity';
import { api } from '../../../api/originApi';

export interface OriginFormProps {
  origin: Origin | null;
  // onSave: (values: Origin) => void;
  onSave: (values: Origin) => Promise<void> | void;  
  onCancel: () => void;
  originTypes: OriginType[];
  parents: Entity[];
  lockPointId?: boolean;
}

// mock translation function? to be replaced by i18n
// const t = (msg: string) => {
//   const parts = msg.split('.');
//   return parts.length === 2 ? parts[1] : msg;  
// };

export const SelectByBool = (v: boolean, t: string, f: string, none: string) => {
  if (v === true) return t;
  if (v === false) return f;
  return none;
}

const OriginForm: React.FC<OriginFormProps> = ({
  origin,
  onSave,
  onCancel,
  originTypes,
  parents,
  lockPointId = false,
}) => {
  const { t } = useTranslation();
  const title = origin ? t('OriginForm.Edit_Origin') : t('OriginForm.Add_New_Origin');
  const { customerId } = useAuthStore();
  const [testResult, setTestResult] = useState<string>('');
  const [testResultVisible, setTestResultVisible] = useState<boolean>(false);
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const defaultCredentialsEditorTemplate: JsonFormTemplate = {
    fields: [
      {
        name: 'jsonString',
        label: t('OriginForm.Credentials_JSON'),
        // label: 'Credentials',
        type: 'json',
        validation: { isJson: true },
        muiProps: {
          multiline: true,
          rows: 10,
          placeholder: t('OriginForm.JSON_Placeholder'),
          // placeholder: 'Placeholder',
        },
      },
    ],
  };

  const initialValues: Origin = origin || {
    customer_id: customerId || -1,
    parent_id: parents.length == 1 ? parents[0].entity_id : -1,
    entity_id: -1,
    origin_type_id: -1,
    name: 'Нова камера',
    credentials: [],
    poling_period_s: 180,
    is_enabled: false,
    is_deleted: false,
    is_enter: false,
    is_exit: false,
    success: false,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(('Name_is_required')),
    parent_id: Yup.number().required(('Parent_is_required')),
    origin_type_id: Yup.number().required(('Origin_type_is_required')),
    credentials: Yup.mixed().required(('credentials_is_required')),
    // credentials: Yup.string().test(
    //   'is-json',
    //   ('Credentials_must_be_valid_JSON'),
    //   (value) => {
    //     if (!value || value.trim() === '' || value.trim() === '{}') return true;
    //     try {
    //       JSON.parse(value);
    //       return true;
    //     } catch (e) {
    //       return false;
    //     }
    //   }
    // ).optional(),
    is_enabled: Yup.boolean().required(),
    poling_period_s: Yup.number()
      .optional()
      .positive(('Polling_period_must_be_positive'))
      .integer(('Polling_period_must_be_integer')),
  });

  if (customerId === -1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{('Loading_form_data')}</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => { // Додаємо async
        // Очищаємо попередню помилку
        setSaveError(null); 
        try {
          await Promise.resolve(onSave(values)); 
        } catch (error) {
          console.error("Помилка збереження Origin:", error);
          const errorMessage = error instanceof Error ? error.message : t('OriginForm.Unknown_Save_Error');
          setSaveError(t('OriginForm.Save_Failed', { error: errorMessage }));
          setSubmitting(false); 
        }
      }}      
      enableReinitialize
    >
      {(formikProps: FormikProps<Origin>) => {
        const { values, setFieldValue } = formikProps;

        const selectedOriginType = originTypes.find(
          (ot) => ot.origin_type_id === values.origin_type_id
        );

        const currentCredentialsTemplate: JsonFormTemplate =
          selectedOriginType?.template || defaultCredentialsEditorTemplate;

        const onTestConnection = async () => {
          setTestResult('Testing...');
          const res = await api.checkConnection(values); // data
          setTestResult(res.result || 'no result');
          setTestResultVisible(true);
        }

        return (
          <Form>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>

            {saveError && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                onClose={() => setSaveError(null)} // Дозволяємо закрити повідомлення
              >
                {saveError}
              </Alert>
            )}
            
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Field name="name">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('OriginForm.Name_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              <Field name="parent_id">
                {({ field, meta }: { field: any; meta: any }) => (
                  <FormControl fullWidth error={meta.touched && !!meta.error}>
                    <InputLabel id="point-id-label">{t('OriginForm.Parent_Label')}</InputLabel>
                    <Select
                      labelId="point-id-label"
                      label={t('OriginForm.Parent_Label')}
                      {...field}
                      disabled={lockPointId}
                    >
                      <MenuItem value=""><em>{t('OriginForm.Select_a_Parent')}</em></MenuItem>
                      {parents.map((parent) => (
                        <MenuItem key={parent.entity_id} value={parent.entity_id}>
                          {parent.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.touched && meta.error && (
                      <Typography color="error" variant="caption" component="div">{meta.error}</Typography>
                    )}
                  </FormControl>
                )}
              </Field>

              <Field name="origin_type_id">
                {({ field, meta }: { field: any; meta: any }) => (
                  <FormControl fullWidth error={meta.touched && !!meta.error}>
                    <InputLabel id="origin-type-id-label">{t('OriginForm.Origin_Type_Label')}</InputLabel>
                    <Select labelId="origin-type-id-label" label={t('OriginForm.Origin_Type_Label')} {...field}>
                      <MenuItem value=""><em>{t('OriginForm.Select_an_Origin_Type')}</em></MenuItem>
                      {originTypes.map((type) => (
                        <MenuItem key={type.origin_type_id} value={type.origin_type_id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.touched && meta.error && (
                      <Typography color="error" variant="caption" component="div">{meta.error}</Typography>
                    )}
                  </FormControl>
                )}
              </Field>

              <Field name="credentials">
                {({ field: fieldPropsForTextField, meta }: FieldProps<string | undefined, Origin>) => (
                  <div>
                    <TextField
                      {...fieldPropsForTextField}
                      fullWidth
                      label={t('OriginForm.Credentials_Label')}
                      multiline
                      rows={3}
                      error={meta.touched && !!meta.error}
                      helperText={(meta.touched && meta.error) || t('OriginForm.Credentials_Helper_Text')}
                      value={JSON.stringify(values.credentials, null, 2)}
                      onChange={(e) => setFieldValue("credentials", JSON.parse(e.target.value))}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsCredentialsModalOpen(true)}
                        startIcon={<EditIcon />}
                      >
                        {t('OriginForm.Edit_JSON_Button')}
                      </Button>
                    </Box>
                  </div>
                )}
              </Field>

              <Field name="is_enabled" type="checkbox">
                {({ field }: { field: any }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={t('OriginForm.Enabled_Label')}
                  />
                )}
              </Field>
              <Field name="success">
                {({ field }: { field: { value: boolean; name: string } }) => (
                  <FormControlLabel
                    control={
                      <Box
                        sx={{
                          // mt: 2,
                          ml: 1.5,
                          mr: 1.5,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: SelectByBool(field.value, 'green', 'red', 'lightgray'),
                        }}
                      />
                    }
                    label={t('OriginForm.succcess')}
                  />
                )}
              </Field>

            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button onClick={onTestConnection} sx={{ mr: 1 }} variant='outlined' >
                  {t('OriginForm.TestConnection')}
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={onCancel} sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined'>
                  {t('OriginForm.Cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}>
                  {formikProps.isSubmitting ? t('OriginForm.Saving') : t('OriginForm.Save_Origin')}
                </Button>
              </Box>
            </Box>

            {testResultVisible && (
            <Box sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    label={t('OriginForm.Test_Result_Label')}
                    multiline
                    rows={3}
                    value={testResult}
                    InputProps={{
                        readOnly: true,
                    }}
                />
            </Box>
            )}

            {isCredentialsModalOpen && (
              <Modal
                open={isCredentialsModalOpen}
                onClose={() => setIsCredentialsModalOpen(false)}
                aria-labelledby="credentials-json-editor-title"
              >
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: { xs: '90%', sm: 600 },
                  bgcolor: 'background.paper',
                  boxShadow: 24,
                  p: { xs: 2, sm: 4 },
                  borderRadius: 1,
                  maxHeight: '90vh',
                  overflowY: 'auto',
                }}
                >
                  {(() => {
                    let jsonFormInitialData: Record<string, any>;
                    const isSingleJsonStringFieldTemplate =
                      currentCredentialsTemplate.fields.length === 1 &&
                      currentCredentialsTemplate.fields[0].type === 'json';

                    if (isSingleJsonStringFieldTemplate) {
                      const fieldName = currentCredentialsTemplate.fields[0].name;
                      jsonFormInitialData = { [fieldName]: values.credentials || '{}' };
                    } else {
                      try {
                        jsonFormInitialData = values.credentials || '{}';
                        if (typeof jsonFormInitialData !== 'object' || jsonFormInitialData === null) {
                          jsonFormInitialData = {};
                        }
                      } catch (e) {
                        console.warn("[OriginForm] Credentials string is not valid JSON. Initializing JsonForm with empty data.", e);
                        jsonFormInitialData = {};
                      }
                    }

                    return (
                      <JsonForm
                        template={currentCredentialsTemplate}
                        initialData={jsonFormInitialData}
                        onSubmit={(data) => {
                          setFieldValue('credentials', data);
                          setIsCredentialsModalOpen(false);
                          return;


                          // ???
                          let finalCredentialsString: string;

                          const wasSingleJsonStringFieldTemplate =
                            currentCredentialsTemplate.fields.length === 1 &&
                            currentCredentialsTemplate.fields[0].type === 'json' &&
                            data.hasOwnProperty(currentCredentialsTemplate.fields[0].name) &&
                            typeof data[currentCredentialsTemplate.fields[0].name] === 'string';

                          if (wasSingleJsonStringFieldTemplate) {
                            finalCredentialsString = data[currentCredentialsTemplate.fields[0].name] as string;
                          } else {
                            finalCredentialsString = JSON.stringify(data);
                          }

                          if (finalCredentialsString.trim() === '') {
                            finalCredentialsString = '{}';
                          }

                          try {
                            JSON.parse(finalCredentialsString);
                          } catch (e) {
                            console.warn("[OriginForm] Produced invalid JSON for credentials after JsonForm submission. Defaulting to '{}'. String was:", finalCredentialsString);
                            finalCredentialsString = '{}';
                          }

                          setFieldValue('credentials', finalCredentialsString);
                          setIsCredentialsModalOpen(false);
                        }}
                        onCancel={() => setIsCredentialsModalOpen(false)}
                        title={t('OriginForm.Edit_Credentials_JSON_Title')}
                        submitButtonText={t('OriginForm.Apply_JSON_Button')}
                        cancelButtonText={t('OriginForm.Cancel')}
                      />
                    );
                  })()}
                </Box>
              </Modal>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default OriginForm;